<template>
  <div space-y-3 max-w-lg role="status">
    <p text-lg text-stone-500>{{ message }}</p>
    <LocationPermissionButton v-if="canAllowHere" @error="buttonError = $event" />
  </div>
</template>
<script setup>
// Shown in place of a page's result when the device location couldn't be found: what went wrong, and where the
// browser supports it, a button to allow location access again.
const props = defineProps({
  // the GeolocationPositionError from the last attempt
  error: { type: Object, required: true },
  // what the location would show, e.g. "the elevation where you are"
  subject: { type: String, required: true },
  // how else to continue, or "" where there is no search box
  alternative: { type: String, default: "or search for a place below" },
});

const isGeolocationElementSupported = useGeolocationElementSupport();

// set when the location button fails; replaces the button with what went wrong, until the next attempt
const buttonError = ref(null);
watch(
  () => props.error,
  () => {
    buttonError.value = null;
  },
);

const code = computed(() => buttonError.value?.code ?? props.error.code);
const canAllowHere = computed(() => code.value === 1 && !buttonError.value && isGeolocationElementSupported.value);

const message = computed(() => {
  const alternative = props.alternative ? `, ${props.alternative}` : "";
  if (code.value !== 1) {
    return `Your device couldn't determine where you are. Try again in a moment${alternative}.`;
  }
  if (buttonError.value) {
    return `Location access is still blocked. Turn it on in your browser settings to see ${props.subject}${alternative}.`;
  }
  if (canAllowHere.value) {
    return `Allow location access to see ${props.subject}${alternative}.`;
  }
  return `Turn on location access for this site in your browser settings to see ${props.subject}${alternative}.`;
});
</script>
